/**
 * @fileoverview Syndicator Showdown — Admin Scratchings Page
 * @module pages/admin/AdminScratchingsPage
 *
 * Tabs: ALL | L1 · Venue R5 | L2 · Venue R6 | …
 * No completion colouring on Scratchings tabs — all neutral.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection, query, orderBy,
  onSnapshot, doc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db }           from '../../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './AdminScratchingsPage.css';

// ─── Race Tab Bar ─────────────────────────────────────────────────────────────

const RaceTabBar = ({ races, activeTab, onTabChange }) => {
  return (
    <div className="ss-scr-tab-bar">
      <button
        type="button"
        className={`ss-scr-tab ${activeTab === 'ALL' ? 'ss-scr-tab--active' : ''}`}
        onClick={() => onTabChange('ALL')}
      >
        All
      </button>

      {races.map((race, index) => {
        const legNum = index + 1;
        const venue  = race.meetingName
          ? race.meetingName.split(' ')[0]
          : '';
        const label  = venue
          ? `L${legNum} · ${venue} R${race.raceNumber}`
          : `L${legNum} · R${race.raceNumber}`;

        // Show scratched runner count as a small badge
        const scratchedCount = (race.runners || []).filter(
          r => r.status?.toLowerCase() === 'scratched'
        ).length;

        return (
          <button
            key={race.id}
            type="button"
            className={`ss-scr-tab ${activeTab === race.id ? 'ss-scr-tab--active' : ''}`}
            onClick={() => onTabChange(race.id)}
            title={`Leg ${legNum}: ${race.meetingName || ''} Race ${race.raceNumber}`}
          >
            {label}
            {scratchedCount > 0 && (
              <span className="ss-scr-tab-badge">{scratchedCount}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ─── Runner Row ───────────────────────────────────────────────────────────────

const RunnerScratchRow = ({ runner, onScratch, onUnscratch }) => {
  const isScratched = runner.status?.toLowerCase() === 'scratched';
  const [confirming, setConfirming] = useState(false);

  return (
    <div className={`ss-scr-runner-row ${isScratched ? 'ss-scr-runner-row--scratched' : ''}`}>
      <div className="ss-scr-runner-num">{runner.runnerNumber}</div>
      <div className="ss-scr-runner-info">
        <span className="ss-scr-runner-name">{runner.name}</span>
        {runner.barrier && (
          <span className="ss-scr-runner-meta">
            Barrier {runner.barrier}
            {runner.jockey ? ` · ${runner.jockey}` : ''}
          </span>
        )}
      </div>
      <div className="ss-scr-runner-status">
        {isScratched
          ? <span className="ss-scr-tag ss-scr-tag--scratched">SCRATCHED</span>
          : <span className="ss-scr-tag ss-scr-tag--active">ACTIVE</span>
        }
      </div>
      <div className="ss-scr-runner-action">
        {confirming ? (
          <div className="ss-scr-confirm-wrap">
            <span className="ss-scr-confirm-label">Confirm scratch?</span>
            <button
              className="ss-scr-btn ss-scr-btn--confirm"
              onClick={() => { onScratch(runner.id); setConfirming(false); }}
              type="button"
            >
              Yes
            </button>
            <button
              className="ss-scr-btn ss-scr-btn--cancel"
              onClick={() => setConfirming(false)}
              type="button"
            >
              No
            </button>
          </div>
        ) : (
          <button
            className={`ss-scr-btn ${isScratched ? 'ss-scr-btn--restore' : 'ss-scr-btn--scratch'}`}
            onClick={() => isScratched ? onUnscratch(runner.id) : setConfirming(true)}
            type="button"
          >
            {isScratched ? '↩ Restore' : '✕ Scratch'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Race Scratch Card ────────────────────────────────────────────────────────

const RaceScratchCard = ({ race, onScratchRunner, onUnScratchRunner }) => {
  const activeCount    = (race.runners || []).filter(r => r.status?.toLowerCase() !== 'scratched').length;
  const scratchedCount = (race.runners || []).filter(r => r.status?.toLowerCase() === 'scratched').length;

  return (
    <div className="ss-scr-race-card">
      <div className="ss-scr-race-header">
        <div>
          <span className="ss-scr-race-badge">R{race.raceNumber}</span>
          <span className="ss-scr-race-name">{race.raceName || `Race ${race.raceNumber}`}</span>
          {race.distance && <span className="ss-scr-race-meta">{race.distance}m</span>}
        </div>
        <div className="ss-scr-race-counts">
          <span className="ss-scr-active-count">{activeCount} active</span>
          {scratchedCount > 0 && (
            <span className="ss-scr-scratched-count">{scratchedCount} scratched</span>
          )}
        </div>
      </div>

      {(race.runners || []).length === 0 ? (
        <div className="ss-scr-no-runners">No runners loaded. Add via Setup or Odds tab.</div>
      ) : (
        <div className="ss-scr-runner-list">
          {(race.runners || [])
            .sort((a, b) => (a.runnerNumber || 0) - (b.runnerNumber || 0))
            .map(runner => (
              <RunnerScratchRow
                key={runner.id}
                runner={runner}
                onScratch={id => onScratchRunner(race.id, race.runners, id)}
                onUnscratch={id => onUnScratchRunner(race.id, race.runners, id)}
              />
            ))
          }
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const AdminScratchingsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const slateId = searchParams.get('slate') || '';

  const [currentUser, setCurrentUser] = useState(null);
  const [slates,      setSlates]      = useState([]);
  const [races,       setRaces]       = useState([]);
  const [statusMsg,   setStatusMsg]   = useState(null);
  const [activeTab,   setActiveTab]   = useState('ALL');
  const [fetchingScr, setFetchingScr] = useState(false);
  const [scrResult,   setScrResult]   = useState(null);

  useEffect(() => { return onAuthStateChanged(auth, setCurrentUser); }, []);

  useEffect(() => {
    const q = query(collection(db, 'game_slates'), orderBy('meetingDate', 'desc'));
    return onSnapshot(q, snap =>
      setSlates(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, []);

  // Active (non-ended, non-archived) slates only — filter client-side
  const activeSlates = useMemo(() =>
    slates
      .filter(s =>
        s.finalised !== true &&
        s.status !== 'finalised' &&
        s.status !== 'completed' &&
        s.status !== 'resulted' &&
        s.archived !== true
      )
      .sort((a, b) => (b.meetingDate || '').localeCompare(a.meetingDate || ''))
  , [slates]);

  // Auto-select first active slate when the current selection is no longer active
  useEffect(() => {
    if (activeSlates.length === 0) return;
    if (slateId && activeSlates.find(s => s.id === slateId)) return;
    setSearchParams({ slate: activeSlates[0].id });
  }, [activeSlates, slateId]);

  useEffect(() => {
    if (!slateId) { setRaces([]); setActiveTab('ALL'); return; }
    const q = query(
      collection(db, 'game_slates', slateId, 'races'),
      orderBy('raceNumber', 'asc')
    );
    setActiveTab('ALL');
    return onSnapshot(q, snap =>
      setRaces(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [slateId]);

  const showStatus = (msg, isError = false) => {
    setStatusMsg({ msg, isError });
    setTimeout(() => setStatusMsg(null), 4000);
  };

  const handleScratchRunner = useCallback(async (raceId, currentRunners, runnerId) => {
    try {
      const updatedRunners = currentRunners.map(r =>
        r.id === runnerId
          ? { ...r, status: 'scratched', scratchedAt: new Date().toISOString() }
          : r
      );
      const batch = writeBatch(db);
      batch.update(doc(db, 'game_slates', slateId, 'races', raceId), {
        runners: updatedRunners, updatedAt: serverTimestamp(),
      });
      await batch.commit();
      const runner = currentRunners.find(r => r.id === runnerId);
      showStatus(`✓ ${runner?.name || 'Runner'} scratched. Players will be notified.`);
    } catch (err) {
      showStatus(`Error: ${err.message}`, true);
    }
  }, [slateId]);

  const handleFetchScratchings = useCallback(async () => {
    if (!slateId) return;
    setFetchingScr(true);
    setScrResult(null);
    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const fetchFn   = httpsCallable(functions, 'fetchScratchings');
      const res       = await fetchFn({ slateId });
      const data      = res.data;
      setScrResult(data);
      if (data.totalScratched > 0 || data.totalJockeyChanges > 0) {
        showStatus(`✓ ${data.message}`);
      } else {
        showStatus(data.message);
      }
    } catch (err) {
      showStatus(`Scratchings fetch failed: ${err.message}`, true);
    } finally {
      setFetchingScr(false);
    }
  }, [slateId]);

  const handleUnScratchRunner = useCallback(async (raceId, currentRunners, runnerId) => {
    try {
      const updatedRunners = currentRunners.map(r =>
        r.id === runnerId ? { ...r, status: 'active', scratchedAt: null } : r
      );
      const batch = writeBatch(db);
      batch.update(doc(db, 'game_slates', slateId, 'races', raceId), {
        runners: updatedRunners, updatedAt: serverTimestamp(),
      });
      await batch.commit();
      const runner = currentRunners.find(r => r.id === runnerId);
      showStatus(`↩ ${runner?.name || 'Runner'} restored.`);
    } catch (err) {
      showStatus(`Error: ${err.message}`, true);
    }
  }, [slateId]);

  // Summary: total scratched across all races
  const totalScratched = useMemo(() =>
    races.reduce((sum, r) =>
      sum + (r.runners || []).filter(run => run.status?.toLowerCase() === 'scratched').length
    , 0)
  , [races]);

  // Which races to render
  const visibleRaces = useMemo(() =>
    activeTab === 'ALL' ? races : races.filter(r => r.id === activeTab)
  , [races, activeTab]);

  if (!currentUser) {
    return <div className="ss-scr-page"><div className="ss-scr-auth">Admin sign-in required.</div></div>;
  }

  return (
    <div className="ss-scr-page">
      <div className="ss-scr-page-header">
        <div>
          <h1 className="ss-scr-page-title">Scratchings</h1>
          <p className="ss-scr-page-sub">
            Mark runners as scratched. Players with equity in scratched runners will be notified.
          </p>
        </div>
        {slateId && (
          <button
            type="button"
            className={`ss-scr-fetch-btn ${fetchingScr ? 'ss-scr-fetch-btn--loading' : ''}`}
            onClick={handleFetchScratchings}
            disabled={fetchingScr}
          >
            {fetchingScr ? (
              <>
                <span className="ss-scr-fetch-spinner" />
                Checking official sources…
              </>
            ) : (
              '🔄 Fetch Official Scratchings'
            )}
          </button>
        )}
      </div>

      {scrResult && (
        <div className={`ss-scr-fetch-result ${scrResult.totalScratched > 0 ? 'ss-scr-fetch-result--found' : ''}`}>
          <strong>{scrResult.message}</strong>
          {scrResult.errors?.length > 0 && (
            <div className="ss-scr-fetch-errors">
              {scrResult.errors.map((e, i) => <div key={i}>⚠ {e}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="ss-scr-warning-banner">
        ⚠ Only scratch confirmed official scratchings from Racing Australia.
      </div>

      <div className="ss-scr-selector-wrap">
        <label className="ss-scr-selector-label">Select game</label>
        <select
          className="ss-scr-selector"
          value={slateId}
          onChange={e => setSearchParams({ slate: e.target.value })}
        >
          <option value="">— Choose a game —</option>
          {activeSlates.map(s => (
            <option key={s.id} value={s.id}>
              {s.gameName || s.id} · {s.meetingDate}
            </option>
          ))}
        </select>
      </div>

      {statusMsg && (
        <div className={`ss-scr-status-msg ${statusMsg.isError ? 'ss-scr-status-msg--error' : ''}`}>
          {statusMsg.msg}
        </div>
      )}

      {slateId && races.length > 0 && (
        <>
          {/* Summary strip */}
          {totalScratched > 0 && (
            <div className="ss-scr-summary-strip">
              {totalScratched} runner{totalScratched !== 1 ? 's' : ''} scratched across {races.length} race{races.length !== 1 ? 's' : ''}
            </div>
          )}

          {/* Tab bar */}
          <RaceTabBar
            races={races}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />

          {/* Race cards — filtered by active tab */}
          {visibleRaces.map(race => (
            <RaceScratchCard
              key={race.id}
              race={race}
              onScratchRunner={handleScratchRunner}
              onUnScratchRunner={handleUnScratchRunner}
            />
          ))}
        </>
      )}

      {slateId && races.length === 0 && (
        <div className="ss-scr-empty">No races found for this game.</div>
      )}
    </div>
  );
};

export default AdminScratchingsPage;
